import { action, makeObservable, observable, runInAction } from 'mobx';

import { inject, injectable } from '@servicetitan/react-ioc';

import { APP_API_TOKEN, IAppApi } from '../../api/app.api';
import { downloadFile } from '../../common';

@injectable()
export class ApiUsageReportPageStore {
    @observable id = 0;
    @observable publicId = '';
    @observable appName = '';
    @observable createdOn: Date | null = null;
    @observable minDate: Date | null = null;
    @observable deleted = false;
    @observable dateRange: { start?: Date | null; end?: Date | null } = {};
    @observable errorBannerVisible = false;
    @observable loading = true;
    @observable downloading = false;

    constructor(@inject(APP_API_TOKEN) private readonly appApi: IAppApi) {
        makeObservable(this);
    }

    @action
    async load(id: number): Promise<void> {
        this.id = id;
        this.publicId = '';
        this.appName = '';
        this.createdOn = null;
        this.minDate = null;
        this.deleted = false;
        this.errorBannerVisible = false;
        this.loading = true;
        try {
            const model = await this.appApi.getApp(id);
            runInAction(() => {
                this.publicId = model.publicId;
                this.appName = model.appName;
                this.createdOn = model.createdOn;
                this.minDate = model.apiUsageReportMinDate;
                this.deleted = model.deleted;
                this.loading = false;
            });
        } catch {
            runInAction(() => {
                this.errorBannerVisible = true;
                this.loading = false;
            });
        }
    }

    async downloadReport(): Promise<boolean> {
        this.downloading = true;
        try {
            const report = await this.appApi.getApiUsageReport(
                this.id,
                this.dateRange.start!,
                this.dateRange.end!,
            );
            const tenants = Object.entries(report.tenants);
            if (tenants.length === 0) {
                return false;
            }

            const content = [
                [
                    'App ID',
                    'Tenant Name',
                    'Tenant ID',
                    'API Name',
                    'HTTP Method',
                    'Endpoint Name',
                    'Successful Calls',
                ].join(','),
                ...tenants
                    .map(tenantEntry =>
                        report.endpoints
                            .map((endpoint, endpointIndex) => ({
                                endpoint,
                                successfulCallCount:
                                    tenantEntry[1].successfulCallCounts[endpointIndex],
                            }))
                            .filter(({ successfulCallCount }) => successfulCallCount > 0)
                            .map(({ endpoint, successfulCallCount }) =>
                                [
                                    this.publicId,
                                    tenantEntry[1].name,
                                    tenantEntry[0],
                                    endpoint.apiName,
                                    endpoint.httpMethod,
                                    endpoint.endpointName,
                                    successfulCallCount.toString(),
                                ].join(','),
                            ),
                    )
                    .flat(1),
                '',
            ].join('\n');
            const fileName =
                [
                    this.appName.replaceAll(/[^- .,0-9A-Z]/gi, '_'),
                    this.publicId,
                    report.firstBinDateTime,
                    report.lastBinDateTime,
                ].join('_') + '.csv';
            downloadFile(content, 'text/csv', fileName);
            return true;
        } finally {
            this.downloading = false;
        }
    }
}
